<template>
    <div class="space-y-4">
        <div class="flex mx-6 space-x-6 pt-4">
            <div class="flex-1 font-medium">
                <h4 class="text-lg my-auto">{{ $t('views.feed.reconciliations.table.header') }}</h4>
            </div>
            <span class="text-right mr-6 w-80">
                <SearchInput v-model:value="search" :label="$t('views.feed.reconciliations.table.searchFieldLabel')"></SearchInput>
            </span>
            <NewReconciliationModal>
                <template #default="{ open }">
                    <AddButton text-class="font-medium" @click="open">
                        {{ $t('views.feed.reconciliations.table.addButtonLabel') }}
                    </AddButton>
                </template>
            </NewReconciliationModal>
        </div>
        <CustomTable :headers="headers" :items="filteredItems">
            <template #from_status_date="{ item, getHeaderClass, getWidth, header }">
                <td :key="header.value" :class="`${getHeaderClass(header, false)} my-auto break-words flex`" :style="`max-width: ${getWidth(header)}px; width: 100%;`">
                    <span>{{ formatDate(item.from_status_date) }}</span>
                </td>
            </template>
            <template #to_status_date="{ item, getHeaderClass, getWidth, header }">
                <td :key="header.value" :class="`${getHeaderClass(header, false)} my-auto break-words flex`" :style="`max-width: ${getWidth(header)}px; width: 100%;`">
                    <span>{{ formatDate(item.to_status_date) }}</span>
                </td>
            </template>
            <template #amount_kg="{ item, getHeaderClass, getWidth, header }">
                <td :key="header.value" :class="`${getHeaderClass(header, false)} my-auto break-words flex`" :style="`max-width: ${getWidth(header)}px; width: 100%;`">
                    <span>{{ formatAmount(item.amount_kg) }}</span>
                </td>
            </template>
            <template #actions="{ item, getHeaderClass, getWidth, header }">
                <td :key="header.value" :class="`${getHeaderClass(header, false)} my-auto break-words flex`" :style="`max-width: ${getWidth(header)}px; width: 100%;`">
                    <EditReconciliationModal :reconciliation="item">
                        <template #default="{ open }">
                            <button type="button" class="p-2 -mr-2" @click="open">
                                <Icon class="h-3 w-3 cursor-pointer" :src="IconSource.Right"></Icon>
                            </button>
                        </template>
                    </EditReconciliationModal>
                </td>
            </template>
        </CustomTable>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { IconSource, TableHeader } from '@/types';
import { store } from '@/plugins/store';
import { useFeedReconciliations } from '@/plugins/store/actions/queries/feedReconciliations';
import CustomTable from '@/components/ui/CustomTable.vue';
import SearchInput from '@/components/ui/SearchInput.vue';
import AddButton from '@/components/ui/AddButton.vue';
import Icon from '@/components/icons/Icon.vue';
import useFilterableTable from '@/composables/useFilterableTable';
import { i18n } from '@/plugins/internationalization/i18n';
import NewReconciliationModal from '@/views/dashboard/feed/feedReconciliations/newReconciliationModal/NewReconciliationModal.vue';
import EditReconciliationModal from '@/views/dashboard/feed/feedReconciliations/editReconciliationModal/EditReconciliationModal.vue';

export default defineComponent({
    components: { CustomTable, SearchInput, AddButton, Icon, NewReconciliationModal, EditReconciliationModal },
    setup() {
        const headers = computed<TableHeader[]>(() => [
            {
                text: i18n.global.t('views.feed.reconciliations.table.headings.locationName'),
                value: 'location.name',
                align: 'left',
                sortable: true,
                searchable: true,
            },
            {
                text: i18n.global.t('views.feed.reconciliations.table.headings.fromStatusDate'),
                value: 'from_status_date',
                align: 'left',
                sortable: true,
                searchable: true,
            },
            {
                text: i18n.global.t('views.feed.reconciliations.table.headings.toStatusDate'),
                value: 'to_status_date',
                align: 'left',
                sortable: true,
                searchable: true,
            },
            {
                text: i18n.global.t('views.feed.reconciliations.table.headings.amountKg'),
                value: 'amount_kg',
                align: 'left',
                sortable: true,
                searchable: false,
                numberFormat: 'decimal',
            },
            {
                text: '',
                value: 'actions',
                align: 'right',
                sortable: false,
                searchable: false,
            },
        ]);

        const siteIds = computed<number[]>(() => store.getters.getSelectedSiteIdsFilteredByPigType);
        const { data } = useFeedReconciliations(siteIds);
        const items = computed(() => data.value ?? []);
        const { search, filteredItems } = useFilterableTable(items, headers);

        function formatDate(dateString: string) {
            return i18n.global.d(new Date(dateString), 'short');
        }

        function formatAmount(amount: number) {
            return i18n.global.n(amount, 'decimal');
        }

        return {
            headers,
            search,
            filteredItems,
            formatDate,
            formatAmount,
            IconSource,
        };
    },
});
</script>
